import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { XMarkIcon, CalendarIcon, ClockIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { sendCalendarInvite } from "../../api/offeredcandidate.api";
import useApiToken from "../../hooks/useApiToken";

/* ─────────────────────────── Helpers ─────────────────────────── */
const parseUtcDate = (value: Date | string) => {
    if (value instanceof Date) return value;
    const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
    return new Date(hasTimezone ? value : `${value}Z`);
};

type CalendarInviteModalProps = {
    isOpen: boolean;
    offerId: string;
    candidateName: string;
    candidateEmail: string;
    buddies: Array<{ id: string; name: string | null; email: string | null }>;
    commencementDate?: Date; // Commencement date to prefill the date field
    existingMeetingDetails?: {
        meetingLink: string;
        meetingTitle: string;
        meetingStartTime: string;
        meetingEndTime: string;
    }; // Existing meeting details for rescheduling
    onClose: () => void;
    onSuccess: () => void;
    isReschedule?: boolean; // New prop to indicate if this is a reschedule
};

const CalendarInviteModal: React.FC<CalendarInviteModalProps> = ({
    isOpen,
    offerId,
    candidateName,
    candidateEmail,
    buddies,
    commencementDate,
    existingMeetingDetails,
    onClose,
    onSuccess,
    isReschedule = false, // Default to false
}) => {
    const token = useApiToken();

    // Initialize with existing meeting details if rescheduling, otherwise use commencement date
    const getInitialDate = () => {
        if (existingMeetingDetails?.meetingStartTime) {
            return parseUtcDate(existingMeetingDetails.meetingStartTime);
        }
        return commencementDate || null;
    };

    const getInitialStartTime = () => {
        if (existingMeetingDetails?.meetingStartTime) {
            return parseUtcDate(existingMeetingDetails.meetingStartTime);
        }
        return null;
    };

    const getInitialEndTime = () => {
        if (existingMeetingDetails?.meetingEndTime) {
            return parseUtcDate(existingMeetingDetails.meetingEndTime);
        }
        return null;
    };

    const [meetingDate, setMeetingDate] = useState<Date | null>(getInitialDate());
    const [startTime, setStartTime] = useState<Date | null>(getInitialStartTime());
    const [endTime, setEndTime] = useState<Date | null>(getInitialEndTime());
    const [meetingTitle, setMeetingTitle] = useState(
        existingMeetingDetails?.meetingTitle ||
        (isReschedule
            ? `RESCHEDULED: Onboarding Meeting - ${candidateName}`
            : `Onboarding Meeting - ${candidateName}`)
    );
    const [meetingDescription, setMeetingDescription] = useState(
        isReschedule
            ? `Rescheduled onboarding meeting for ${candidateName} with assigned buddy/buddies.`
            : `Welcome onboarding meeting for ${candidateName} with assigned buddy/buddies.`
    );
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update meeting date when commencementDate or existingMeetingDetails changes
    React.useEffect(() => {
        if (isOpen) {
            if (existingMeetingDetails?.meetingStartTime) {
                const startDate = parseUtcDate(existingMeetingDetails.meetingStartTime);
                const endDate = parseUtcDate(existingMeetingDetails.meetingEndTime);
                setMeetingDate(startDate);
                setStartTime(startDate);
                setEndTime(endDate);
                setMeetingTitle(existingMeetingDetails.meetingTitle);
            } else if (commencementDate && !meetingDate) {
                setMeetingDate(commencementDate);
            }
        }
    }, [isOpen, commencementDate, existingMeetingDetails]);

    if (!isOpen) return null;

    const handleClose = () => {
        setMeetingDate(null);
        setStartTime(null);
        setEndTime(null);
        setMeetingTitle(
            isReschedule
                ? `RESCHEDULED: Onboarding Meeting - ${candidateName}`
                : `Onboarding Meeting - ${candidateName}`
        );
        setMeetingDescription(
            isReschedule
                ? `Rescheduled onboarding meeting for ${candidateName} with assigned buddy/buddies.`
                : `Welcome onboarding meeting for ${candidateName} with assigned buddy/buddies.`
        );
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        // Validation
        if (!meetingDate) {
            setError("Please select a meeting date");
            return;
        }
        if (!startTime) {
            setError("Please select a start time");
            return;
        }
        if (!endTime) {
            setError("Please select an end time");
            return;
        }
        if (endTime <= startTime) {
            setError("End time must be after start time");
            return;
        }
        if (!meetingTitle.trim()) {
            setError("Please enter a meeting title");
            return;
        }

        // Combine date and time
        const startDateTime = new Date(meetingDate);
        startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

        const endDateTime = new Date(meetingDate);
        endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

        try {
            setIsSubmitting(true);
            await sendCalendarInvite({
                offerId,
                candidateEmail,
                candidateName,
                buddyEmails: buddies.map(b => b.email!).filter(Boolean),
                startDateTime,
                endDateTime,
                subject: meetingTitle,
                body: meetingDescription,
            }, token!);

            onSuccess();
            handleClose();
        } catch (err: any) {
            setError(err.message || "Failed to send calendar invite");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[80] overflow-y-auto">
            <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-2xl w-full my-8 overflow-hidden border border-transparent dark:border-gray-700">
                {/* Modal Header */}
                <div className="bg-white dark:bg-darkbglight border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                            <CalendarIcon className="h-6 w-6 text-entntblue" />
                            {isReschedule ? "Reschedule Calendar Invite" : "Send Calendar Invite"}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {isReschedule ? "Update meeting time for" : "Schedule onboarding meeting with"} {candidateName}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Attendees Info */}
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                            Meeting Attendees
                        </h4>
                        <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
                            <p>
                                <span className="font-medium">Candidate:</span> {candidateName} ({candidateEmail})
                            </p>
                            <p>
                                <span className="font-medium">Buddies:</span>{" "}
                                {buddies.map((b, idx) => (
                                    <span key={b.id}>
                                        {b.name} ({b.email}){idx < buddies.length - 1 && ", "}
                                    </span>
                                ))}
                            </p>
                        </div>
                    </div>

                    {/* Meeting Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Meeting Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                            placeholder="Enter meeting title"
                            disabled={isSubmitting}
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-entntblue focus:border-entntblue outline-none transition-all"
                        />
                    </div>

                    {/* Meeting Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Meeting Description
                        </label>
                        <textarea
                            rows={3}
                            value={meetingDescription}
                            onChange={(e) => setMeetingDescription(e.target.value)}
                            placeholder="Enter meeting description or agenda"
                            disabled={isSubmitting}
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-entntblue focus:border-entntblue outline-none transition-all resize-none"
                        />
                    </div>

                    {/* Date and Time Selection */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                                <CalendarIcon className="h-4 w-4" /> Date <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                selected={meetingDate}
                                onChange={(date) => setMeetingDate(date)}
                                minDate={new Date()}
                                placeholderText="Select date"
                                disabled={isSubmitting}
                                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-entntblue focus:border-entntblue outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                                <ClockIcon className="h-4 w-4" /> Start Time <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                selected={startTime}
                                onChange={(time) => setStartTime(time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="Start time"
                                disabled={isSubmitting}
                                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-entntblue focus:border-entntblue outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                                <ClockIcon className="h-4 w-4" /> End Time <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                selected={endTime}
                                onChange={(time) => setEndTime(time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="End time"
                                disabled={isSubmitting}
                                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-entntblue focus:border-entntblue outline-none transition-all"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg">
                            {error}
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 dark:bg-darkbglight px-6 py-4 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-2.5 bg-entntblue text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all flex items-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {isReschedule ? "Rescheduling..." : "Sending..."}
                            </>
                        ) : (
                            <>
                                <PaperAirplaneIcon className="h-5 w-5" />
                                {isReschedule ? "Reschedule Meeting" : "Send Calendar Invite"}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CalendarInviteModal;
